import { appAxiosAuthInstance } from '@/config/axios';
import {
	getStructuredChangeExtraCreditsResponse,
	getStructuredCreateCheckoutSessionResponse,
	getStructuredCurrentPlanResponse,
	getStructuredExtraCreditsChargePreviewResponse,
	getStructuredFetchExtraCreditsResponse,
	getStructuredFetchPlansResponse,
	getStructuredGetCustomerPortalLinkResponse,
	getStructuredUpgradePlanResponse,
	getStructuredVerifyPaymentResponse,
} from '@/models/payment';

import { getUserTimezone } from '@/utils/date';

import {
	ChangeExtraCreditsPayload,
	CreateCheckoutSessionPayload,
	FetchExtraCreditsPayload,
	GetCurrentPlanPayload,
	IChangeExtraCreditsResponse,
	ICreateCheckoutSessionResponse,
	ICurrentPlanResponse,
	IExtraCreditsChargePreviewResponse,
	IFetchExtraCreditsResponse,
	IFetchPlansResponse,
	IGetCustomerPortalLinkResponse,
	IUpgradePlanResponse,
	IVerifyPaymentResponse,
	UpgradePlanPayload,
	VerifyPaymentPayload,
} from './payment.types';

export async function createCheckoutSession(payload: CreateCheckoutSessionPayload) {
	const currentTimezone = getUserTimezone();

	const res = await appAxiosAuthInstance.post('/payment/create-checkout-session', {
		...payload,
		zone_id: currentTimezone,
	});
	const data = res.data as ICreateCheckoutSessionResponse;
	return getStructuredCreateCheckoutSessionResponse(data);
}

export async function verifyPayment(payload: VerifyPaymentPayload) {
	const res = await appAxiosAuthInstance.post('/payment/verify-payment', payload);
	const data = res.data as IVerifyPaymentResponse;
	return getStructuredVerifyPaymentResponse(data);
}

export async function getCurrentPlan(payload: GetCurrentPlanPayload) {
	const res = await appAxiosAuthInstance.post('/page/fetch-plan', {
		...payload,
		include_overdue_status: true,
	});
	const data = res.data as ICurrentPlanResponse;
	return getStructuredCurrentPlanResponse(data);
}

export async function getCustomerPortalLink(pageId: string, isOverdue?: boolean) {
	const res = await appAxiosAuthInstance.post('/user/get-customer-portal-url', {
		page_id: pageId,
		...(isOverdue && { is_overdue: true }),
	});
	const data = res.data as IGetCustomerPortalLinkResponse;
	return getStructuredGetCustomerPortalLinkResponse(data);
}

export async function upgradePlan(payload: UpgradePlanPayload) {
	const currentTimezone = getUserTimezone();

	const res = await appAxiosAuthInstance.post('/payment/upgrade-plan', {
		...payload,
		zone_id: currentTimezone,
	});
	const data = res.data as IUpgradePlanResponse;
	return getStructuredUpgradePlanResponse(data);
}

export async function fetchExtraCredits(payload: FetchExtraCreditsPayload) {
	const res = await appAxiosAuthInstance.post('/payment/fetch-add-ons', payload);
	const data = res.data as IFetchExtraCreditsResponse;
	return getStructuredFetchExtraCreditsResponse(data);
}

export async function changeExtraCredits(payload: ChangeExtraCreditsPayload) {
	const res = await appAxiosAuthInstance.post('/payment/change-add-on', payload);
	const data = res.data as IChangeExtraCreditsResponse;
	return getStructuredChangeExtraCreditsResponse(data);
}

export async function previewExtraCreditsChange(payload: ChangeExtraCreditsPayload) {
	const res = await appAxiosAuthInstance.post('/payment/preview-add-on-change', payload);
	const data = res.data as IExtraCreditsChargePreviewResponse;
	return getStructuredExtraCreditsChargePreviewResponse(data);
}

export async function fetchPlans() {
	const res = await appAxiosAuthInstance.post('/payment/fetch-plans', {});
	const data = res.data as IFetchPlansResponse;
	return getStructuredFetchPlansResponse(data);
}
